import React, { useMemo } from "react";
import {
    CartesianGrid,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { HistoryRange, RttHistoryPoint } from "./requests/request";
import {
    AXIS_LABEL_STYLE,
    AXIS_TICK,
    BASELINE_COLOR,
    GRID_STROKE,
    RTT_COLOR,
    TOOLTIP_CONTENT_STYLE,
    TOOLTIP_LABEL_STYLE,
    BASELINE_OPACITY,
    TYPICAL_DASH,
    UNUSUAL_DASH,
    formatPointTime,
    rttDomain,
} from "./historyChart";

interface RttChartProps {
    points: RttHistoryPoint[];
    range: HistoryRange;
    /** What the link normally does; null when it has no answered probe. */
    typicalMs: number | null;
    /** What counts as a bad window for this link; null likewise. */
    unusualMs: number | null;
}

/**
 * Mean RTT of the answered probes per window, read against the link's own
 * normal operating range: the point of the view is spotting a line that
 * drifts above what this link usually does, which neither a last-RTT
 * display nor a single threshold can show.
 *
 * The reference lines carry no in-chart labels on purpose: on a steady link
 * the two percentiles land 0.1 ms apart (measured), leaving no room for a
 * pair of them. The legend above the chart names them instead.
 */
const RttChart: React.FC<RttChartProps> = ({
    points,
    range,
    typicalMs,
    unusualMs,
}) => {
    const chartData = useMemo(
        () =>
            points.map((point) => ({
                formattedTime: formatPointTime(point.time, range),
                rttMs: point.rttMs,
                samples: point.samples,
            })),
        [points, range]
    );

    const domain = useMemo(
        () =>
            rttDomain(
                chartData.map((point) => point.rttMs),
                [typicalMs, unusualMs]
            ),
        [chartData, typicalMs, unusualMs]
    );



    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart
                data={chartData}
                margin={{ top: 16, right: 24, left: 8, bottom: 8 }}
            >
                <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke={GRID_STROKE}
                />
                <XAxis
                    dataKey="formattedTime"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={AXIS_TICK}
                    interval="preserveStartEnd"
                />
                <YAxis
                    domain={domain}
                    tickFormatter={(value: number) => value.toFixed(0)}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={AXIS_TICK}
                    label={{
                        value: "RTT (ms)",
                        angle: -90,
                        position: "insideLeft",
                        style: AXIS_LABEL_STYLE,
                    }}
                />
                <Tooltip
                    formatter={(value: number) => [
                        value == null || Number.isNaN(value)
                            ? "—"
                            : `${value.toFixed(2)} ms`,
                        "Mean RTT",
                    ]}
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                />
                {typicalMs !== null && (
                    <ReferenceLine
                        y={typicalMs}
                        stroke={BASELINE_COLOR}
                        strokeOpacity={BASELINE_OPACITY}
                        strokeDasharray={TYPICAL_DASH}
                        ifOverflow="extendDomain"
                    />
                )}
                {unusualMs !== null && (
                    <ReferenceLine
                        y={unusualMs}
                        stroke={BASELINE_COLOR}
                        strokeOpacity={BASELINE_OPACITY}
                        strokeDasharray={UNUSUAL_DASH}
                        strokeLinecap="round"
                        ifOverflow="extendDomain"
                    />
                )}
                <Line
                    type="monotone"
                    dataKey="rttMs"
                    name="Mean RTT"
                    stroke={RTT_COLOR}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    connectNulls={false}
                />
            </LineChart>
        </ResponsiveContainer>
    );
};

export default RttChart;
